import { useCallback, useEffect, useRef } from 'react'
import { useExplorer } from "../../providers"
import { ExploreWidgetConfig, SortOptionKey } from "../../config"
import { resolveDefaultSort, resolveSortOptions } from "../../sort"
import { useWidget } from "../../../../providers/widget"

// Module short labels (r8 #937, toolkit Shortcode.php:762-777): compact
// "X: Low to High" pairs sized for the closed pill — not the verbose
// parenthesized forms the legacy select carried. The lowest/average price
// variants are widget-only sorts; they follow the same label grammar.
//
// Labels only — the menu ORDER comes from `sortOptionKeys` via
// `resolveSortOptions`, and the `Record<SortOptionKey, string>` type makes a
// key added to the schema without a label a compile error. (The dashboard's
// SORT_OPTION_LABELS is a separate, deliberately admin-facing wording.)
const SORT_LABELS: Record<SortOptionKey, string> = {
  featured: 'Featured',
  created_desc: 'Newest First',
  created_asc: 'Oldest First',
  title_asc: 'Name: A to Z',
  title_desc: 'Name: Z to A',
  base_price_asc: 'Price: Low to High',
  base_price_desc: 'Price: High to Low',
  lowest_price_asc: 'Lowest Price: Low to High',
  lowest_price_desc: 'Lowest Price: High to Low',
  average_price_asc: 'Average Price: Low to High',
  average_price_desc: 'Average Price: High to Low',
  bedrooms_asc: 'Bedrooms: Low to High',
  bedrooms_desc: 'Bedrooms: High to Low',
  guests_asc: 'Guests: Low to High',
  guests_desc: 'Guests: High to Low',
  rating_desc: 'Rating: High to Low',
  rating_asc: 'Rating: Low to High',
  random: 'Random',
}

// Restates the select's CSS padding-right (the chevron zone) for the width
// measurement below — keep in sync with `.hfse-sbp-results-sort select`.
const SORT_CHEVRON_PAD_PX = 18

/**
 * "Sort by: Featured ⌄" pill — module parity (r8 #937; searchbar-pro.js:
 * 4568-4635, css:2478-2523). The <label> wrapper IS the control chrome: the
 * select inside is borderless with a hand-drawn chevron, the focus ring rides
 * :focus-within on the wrapper, and the closed pill is sized to the SELECTED
 * option (module sizeSortSelect) instead of the browser default (the longest
 * option), so "Featured" doesn't wear "Average Price: High to Low" width.
 *
 * Functional wiring is unchanged from the legacy select: sort is a live
 * `visitorFilters.sort_by` write riding the same query the grid uses — the
 * widget equivalent of the module's name="sort" select, which core's own
 * change handler picks up for its fetch params (searchbar-pro.js:4568-4572).
 */
export default function SortDropdown() {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter } = useExplorer()
  const selectRef = useRef<HTMLSelectElement>(null)
  const measureCtx = useRef<CanvasRenderingContext2D | null | undefined>(undefined)

  // Curate + order the sort menu to the configured list (WP: orderby_options), and
  // resolve the default with the #557 fallback. Both live in config.ts because the
  // browse query needs the same answer — `withBrowsePriceField` sends it as `sort_by`
  // on the very first request, SSR included (#1093). This control only DISPLAYS it.
  const availableOptions = resolveSortOptions(options.sort)
  const effectiveDefault = resolveDefaultSort(options.sort)
  const currentSort = state.visitorFilters.sort_by ?? effectiveDefault
  const currentLabel = SORT_LABELS[currentSort as SortOptionKey] ?? ''

  // Pin the closed pill to the selected option's width (module sizeSortSelect,
  // js:4603-4621): canvas metrics against the select's computed font. Without
  // this the select sizes to its LONGEST option in every browser.
  const sizeSelect = useCallback((label: string) => {
    const sel = selectRef.current
    if (!sel || !label) return
    if (measureCtx.current === undefined) {
      measureCtx.current = document.createElement('canvas').getContext('2d')
    }
    const ctx = measureCtx.current
    // No 2d context (some locked-down webviews): leave the browser default
    // width — wide, but never clipped.
    if (!ctx) return
    const cs = window.getComputedStyle(sel)
    ctx.font = cs.font || `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
    const w = Math.ceil(ctx.measureText(label).width) + SORT_CHEVRON_PAD_PX
    if (!isFinite(w) || w <= 0) return
    sel.style.width = `${w}px`
  }, [])

  useEffect(() => {
    sizeSelect(currentLabel)
    // Fallback-face metrics are narrower than the real font's, so a width
    // pinned before webfonts settle can clip (same race the submit CTA
    // measurement handles; module js:4634 re-measures on fonts.ready too).
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts
    let cancelled = false
    if (fonts?.ready && typeof fonts.ready.then === 'function') {
      fonts.ready.then(() => { if (!cancelled) sizeSelect(currentLabel) })
    }
    return () => { cancelled = true }
  }, [currentLabel, sizeSelect])

  // Module rule (Shortcode.php:799): a sort menu needs a choice — with fewer
  // than two options the control renders nothing at all.
  if (availableOptions.length < 2) return null

  return (
    <label
      className="hfse-sbp-results-sort"
      onClick={(e) => {
        // A <label> wrapping a <select> only FOCUSES it on click — it never
        // opens the menu. Forward the click (module js:4624-4633).
        const sel = selectRef.current
        if (!sel || e.target === sel) return
        sel.focus()
        try {
          (sel as HTMLSelectElement & { showPicker?: () => void }).showPicker?.()
        } catch {
          // NotAllowedError (no user gesture / cross-origin frame) — focus
          // alone still leaves the select fully keyboard-operable.
        }
      }}
    >
      <span className="hfse-sbp-results-sort-prefix">Sort by:</span>
      <select
        ref={selectRef}
        name="sort"
        aria-label="Sort results"
        value={currentSort}
        onChange={e => setFilter({ sort_by: e.target.value })}
      >
        {availableOptions.map(value => (
          <option key={value} value={value}>
            {SORT_LABELS[value]}
          </option>
        ))}
      </select>
    </label>
  )
}
